import React, { memo } from 'react';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';

const ScatterChart = (props) => {
  const {
    Axisinterval,
    ChartData,
    YaxisLableConfig,
    chartheight,
    events,
    maxCommitCount,
    onChartClick,
    xAxisLabels,
    otherXAxisProperty,
    otherCustomProperty,
    otherYAxisProperty,
    xAxisNameGap,
    ynamegap,
    XaisnameGapformetter,
  } = props;


  ////Yaxis name gap 
  const formatYAxis = (value) => {
    if (value >= 1_000_000_000_000) { return `${value / 1_000_000_000_000}T`; }// Convert to T (trillions)
    if (value >= 1_000_000_000) { return `${value / 1_000_000_000}B`; } // Convert to B (billions)
    if (value >= 1_000_000) { return `${value / 1_000_000}M`; } // Convert to M (millions)
    if (value >= 1_000) { return `${value / 1_000}K`; } // Convert to K (thousands)
    return value;
  };
  const formattedValues = (Array.isArray(XaisnameGapformetter))
    ? XaisnameGapformetter.map((value) => Math.floor(formatYAxis(value)))
    : XaisnameGapformetter.flatMap((obj) => obj.data).map((value) => Math.floor(formatYAxis(value)));




  const maxDigits = Math.max(
    ...formattedValues.map((value) => value.toString().length)
  );

  const nameGapYAxis = (maxDigits) => {
    let nameGap = 27; // Default value for 2-digit numbers
    if (maxDigits === 3) { nameGap = 40; }
    else if (maxDigits === 4) { nameGap = 45; }
    else if (maxDigits === 5) { nameGap = 48; }
    else if (maxDigits === 6) { nameGap = 50; }
    else if (maxDigits === 7) { nameGap = 52; }
    else if (maxDigits === 13) { nameGap = 77; }
    return nameGap;
  };
  ///////

  // to get initials from name eg : Arivalagan S => AS
  const getInitials = (name) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();

  const positionCountMap = new Map();

  // Count overlapping points
  const allPoints = ChartData.flatMap((user) =>
    user.data.map(([x, y]) => `${x}-${y}`)
  );
  const pointCounts = allPoints.reduce((acc, key) => {
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // ✅ Build max Y value per X index
  const maxYPerX = new Map();
  ChartData.forEach((user) => {
    user.data.forEach(([x, y]) => {
      const currentMax = maxYPerX.get(x) || 0;
      if (y > currentMax) {
        maxYPerX.set(x, y);
      }
    });
  });

  const createUserSeries = (name, bgColor, textColor, data, imageUrl) => {
    const adjustedData = data.map(([x, y]) => {
      const key = `${x}-${y}`;
      const currentCountAtPoint = positionCountMap.get(key) || 0;
      const totalUsersAtPoint = pointCounts[key] || 1;
      const offset = (currentCountAtPoint - (totalUsersAtPoint - 1) / 2) * 30;
      positionCountMap.set(key, currentCountAtPoint + 1);
      return { x, y, offset };
    });

    return {
      type: 'custom',
      name,
      renderItem: function (params, api) {
        const xVal = api.value(0);
        let yVal = api.value(1);
        const offset = api.value(2);

        // ✅ Dynamic uplift for y === 0 based on max value for that month
        if (yVal === 0) {
          const maxY = maxYPerX.get(xVal) || 1;
          yVal += maxY * 0.08;
        }

        const coord = api.coord([xVal, yVal]);

        const circleRadius = 24;
        const imageSize = 44;
        const imageRadius = imageSize / 2;

        const circle = {
          type: 'circle',
          shape: {
            cx: coord[0] + offset,
            cy: coord[1],
            r: circleRadius,
          },
          style: {
            fill: bgColor,
          },
        };

        if (imageUrl) {
          return {
            type: 'group',
            children: [
              circle,
              {
                type: 'image',
                style: {
                  image: imageUrl,
                  x: coord[0] + offset - imageRadius,
                  y: coord[1] - imageRadius,
                  width: imageSize,
                  height: imageSize,
                },
                clipPath: {
                  type: 'circle',
                  shape: {
                    cx: coord[0] + offset,
                    cy: coord[1],
                    r: imageRadius,
                  },
                },
              },
            ],
          };
        } else {
          const initials = getInitials(name);
          return {
            type: 'group',
            children: [
              circle,
              {
                type: 'text',
                style: {
                  text: initials,
                  x: coord[0] + offset,
                  y: coord[1],
                  textAlign: 'center',
                  textVerticalAlign: 'middle',
                  font: 'bold 12px sans-serif',
                  fill: textColor,
                },
              },
            ],
          };
        }
      },
      encode: { x: 'x', y: 'y' },
      data: adjustedData.map((d) => [d.x, d.y, d.offset]),
      ...otherCustomProperty
    };
  };

  // Define color pairs for different users (background and text/initials)
  const colorPairs = [
    ['#f8d5e1', '#711d40'],
    ['#e3f2fd', '#0d47a1'],
    ['#ffe0b2', '#e65100'],
    ['#c8e6c9', '#1b5e20'],
    ['#f3e5f5', '#4a148c'],
    ['#fff9c4', '#f57f17'],
    ['#d1c4e9', '#311b92'],
    ['#b2ebf2', '#006064'],
    ['#fbe9e7', '#bf360c'],
    ['#f0f4c3', '#827717'],
  ];

  // Create series for each user based on their data and assigned colors
  const userSeries = ChartData.map((u, index) =>
    createUserSeries(
      u.name,
      colorPairs[index % colorPairs.length][0], // Background color
      colorPairs[index % colorPairs.length][1], // Text color
      u.data,
      u.imageUrl
    )
  );

  // ECharts option configuration
  const option = {
    tooltip: {
      trigger: 'item', // Trigger tooltip on item hover
      formatter: function (params) {
        const seriesName = params.seriesName.replace(/\s{0,10}\([^)]{0,200}\)\s{0,10}/g, '');

        return `${params.name}<br/>${seriesName}<br/>Commits: ${params.value[1]}`;
      },
    },
    legend: {
      data: ChartData.map((u) => u.name),
      type: 'scroll',
      show: false,
      orient: 'horizontal',
      left: 'left',
    },
    xAxis: {
      data: xAxisLabels,
      type: 'category',
      show: true,
      nameLocation: 'middle',
      axisLine: { show: true },
      axisTick: { show: true },
      name: 'Months',
      nameGap: xAxisNameGap ? xAxisNameGap : 30,
      ...otherXAxisProperty
    },
    yAxis: {
      name: 'Commits',
      type: 'value',
      nameLocation: 'middle',
      axisLabel: {
        margin: 50,
        lineHeight: 50,
      },
      nameGap: ynamegap ? ynamegap : nameGapYAxis(maxDigits),
      interval: Axisinterval || null,
      axisLabel: YaxisLableConfig
        ? { ...YaxisLableConfig }
        : {
          formatter: function (value) {
            return Number.isInteger(value) ? value : '';
          },
        },
      ...otherYAxisProperty
    },
    series: [
      ...userSeries, // Include all user series
      {
        type: 'bar',
        z: 0, // Render behind other series
        itemStyle: { color: '#979797' }, // Color of the bar
        step: 'start',
        smooth: true,
        barWidth: 2.5, // Width of the bar
        symbol: 'none', // No symbols on this series
        tooltip: { show: false }, // Hide tooltip for this series
        data: maxCommitCount,
      },
    ],
  };

  const handleChartReady = (chart) => { };

  const handleLegendSelectChanged = (params) => { };

  const onEvents = {
    click: onChartClick,
    legendselectchanged: handleLegendSelectChanged,
  };

  return (
    <div>
      <div style={{ height: chartheight || 400 }}>
        <ReactECharts
          option={option}
          onEvents={events ? onEvents : {}} // Apply event handlers if 'events' prop is true
          onChartReady={handleChartReady} // Callback when chart is initialized
        />
      </div>
    </div>
  );
};

ScatterChart.propTypes = {
  Axisinterval: PropTypes.number,
  ChartData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(
        PropTypes.arrayOf(PropTypes.number) // [x, y]
      ).isRequired,
    })
  ).isRequired,
  YaxisLableConfig: PropTypes.object,
  otherXAxisProperty: PropTypes.object,
  ynamegap: PropTypes.number,
  otherCustomProperty: PropTypes.object,
  otherYAxisProperty: PropTypes.object,
  xAxisNameGap: PropTypes.number,
  chartheight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  events: PropTypes.bool,
  maxCommitCount: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.object])
  ),
  onChartClick: PropTypes.func,
  XaisnameGapformetter: PropTypes.array,
  xAxisLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default memo(ScatterChart);



